import { useEffect, useId, useRef, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import { twMerge } from 'tailwind-merge';
import { DateRange, formatDate, parseApiLocalDate, parseFormDate } from '../utils/date';
import {
  FILTER_PANEL_CLASSES,
  FILTER_PILL_CLASSES,
  FILTER_PILL_CLEAR_CLASSES,
} from './filter-dropdown';
import 'react-datepicker/dist/react-datepicker.css';

export interface DateRangeFilterProps {
  /** What the dates are of: "Created", "Updated". */
  label: string;
  value: DateRange | undefined;
  /** Undefined once both ends are empty. */
  onChange: (value: DateRange | undefined) => void;
  testId?: string;
}

/** The picked range in the pill: "Oct 1 – Oct 31", "from Oct 1" or "to Oct 31". */
const rangeText = ({ from, to }: DateRange): string => {
  if (from && to) return `${formatDate(from, 'MMM d')} – ${formatDate(to, 'MMM d')}`;
  return from ? `from ${formatDate(from, 'MMM d')}` : `to ${formatDate(to, 'MMM d')}`;
};

/** Drops empty ends; undefined when nothing is left. */
const cleanRange = (range: DateRange): DateRange | undefined => {
  const next: DateRange = {};
  if (range.from) next.from = range.from;
  if (range.to) next.to = range.to;
  return next.from || next.to ? next : undefined;
};

/**
 * A filter-bar pill for a from / to date range (used by ListFilter's `dateFilters`).
 * Either end may be left empty: "from Oct 1" and "to Oct 31" are both ranges. Each pick
 * applies straight away, like the option pills.
 */
export function DateRangeFilter({ label, value, onChange, testId }: DateRangeFilterProps) {
  const buttonId = useId();
  const panelId = useId();
  const fromId = useId();
  const toId = useId();
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const range = value ?? {};
  const hasValue = Boolean(range.from || range.to);
  const fromDate = parseApiLocalDate(range.from);
  const toDate = parseApiLocalDate(range.to);

  // Close on an outside click
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (open && popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    if (open) document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const setEnd = (end: keyof DateRange, date: Date | null) =>
    onChange(cleanRange({ ...range, [end]: parseFormDate(date) ?? undefined }));

  return (
    <div className="tw:relative" ref={popoverRef} data-testid={testId}>
      {/* The pill is a frame around two sibling buttons (open, clear): a button may not
          sit inside another button. */}
      <div className={twMerge(FILTER_PILL_CLASSES, 'tw:gap-0 tw:p-0')}>
        <button
          id={buttonId}
          type="button"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen((o) => !o)}
          className="tw:flex tw:items-center tw:gap-2 tw:!rounded-lg tw:border-0 tw:bg-transparent tw:px-3 tw:py-1.5 tw:!text-zinc-700 tw:focus:outline-none"
          data-testid={testId && `${testId}-pill`}
        >
          <i className="far fa-calendar tw:text-zinc-500" aria-hidden="true" />
          <span className="tw:truncate">{hasValue ? `${label}: ${rangeText(range)}` : label}</span>
          <i className="fas fa-chevron-down tw:text-zinc-500" />
        </button>
        {hasValue && (
          <button
            type="button"
            aria-label={`Clear ${label}`}
            onClick={() => onChange(undefined)}
            className={twMerge(FILTER_PILL_CLEAR_CLASSES, 'tw:ml-0 tw:py-1.5 tw:pr-3')}
            data-testid={testId && `${testId}-clear`}
          >
            <i className="fas fa-xmark" />
          </button>
        )}
      </div>

      {open && (
        <div id={panelId} role="dialog" aria-labelledby={buttonId} className={FILTER_PANEL_CLASSES}>
          <div className="tw:flex tw:items-center tw:justify-between tw:gap-3 tw:px-3 tw:py-2 tw:border-b tw:border-zinc-100">
            <div className="tw:text-sm tw:font-medium tw:whitespace-nowrap tw:text-brand-green-0">
              Filter by {label} date
            </div>
            <button
              type="button"
              className="tw:text-xs tw:text-zinc-500 tw:hover:text-brand-green-1"
              onClick={() => onChange(undefined)}
            >
              Clear
            </button>
            <button
              type="button"
              aria-label="Close"
              className="tw:text-xs tw:text-zinc-500 tw:hover:text-brand-green-1"
              onClick={() => setOpen(false)}
            >
              <i className="fas fa-times" />
            </button>
          </div>
          <div className="tw:flex tw:flex-col tw:gap-2 tw:p-3">
            {/* Bootstrap's reboot styles bare labels; force the size and margin. */}
            <div className="tw:flex tw:flex-col tw:gap-1">
              <label htmlFor={fromId} className="tw:!mb-0 tw:text-xs tw:text-zinc-500">
                From
              </label>
              <ReactDatePicker
                id={fromId}
                className="datepicker-input"
                wrapperClassName="datepicker-wrapper"
                selected={fromDate}
                onChange={(date) => setEnd('from', date)}
                selectsStart
                startDate={fromDate}
                endDate={toDate}
                maxDate={toDate ?? undefined}
                placeholderText="Any date"
                dateFormat="MM/dd/yyyy"
                isClearable
              />
            </div>
            <div className="tw:flex tw:flex-col tw:gap-1">
              <label htmlFor={toId} className="tw:!mb-0 tw:text-xs tw:text-zinc-500">
                To
              </label>
              <ReactDatePicker
                id={toId}
                className="datepicker-input"
                wrapperClassName="datepicker-wrapper"
                selected={toDate}
                onChange={(date) => setEnd('to', date)}
                selectsEnd
                startDate={fromDate}
                endDate={toDate}
                minDate={fromDate ?? undefined}
                placeholderText="Any date"
                dateFormat="MM/dd/yyyy"
                isClearable
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DateRangeFilter;
